import React, { useMemo, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { useModules } from "newui/features/RolesPermissions/hooks/useRoleMatrix";
import { useUserEffectivePermissions } from "newui/features/RolesPermissions/hooks/useUserEffectivePermissions";
import { useUserAssignments } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type {
	EffectiveCapabilityFlags,
	PermissionLevel,
	ScopeType,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface UserAccessMatrixModalProps {
	isOpen: boolean;
	userId?: number;
	userName?: string;
	onClose: () => void;
}

interface LevelDisplay {
	fillClassName: string;
	label: string;
	textClassName: string;
}

type AccessTab = "company" | "scoped";

// Mirrors PermissionMatrixEditor's LEVEL_CONFIG so the read-only view reads with
// the same colour language as the editable matrix (server-authoritative levels).
const LEVEL_DISPLAY: Record<PermissionLevel, LevelDisplay> = {
	full: { label: "Full", fillClassName: "bg-emerald-100 border-emerald-300", textClassName: "text-emerald-800" },
	view: { label: "View", fillClassName: "bg-blue-100 border-blue-300", textClassName: "text-blue-800" },
	comment: { label: "Comment", fillClassName: "bg-amber-100 border-amber-300", textClassName: "text-amber-900" },
	event: { label: "Event only", fillClassName: "bg-slate-100 border-slate-300", textClassName: "text-slate-700" },
	none: { label: "No access", fillClassName: "bg-gray-50 border-gray-200", textClassName: "text-gray-400" },
};

// Column order for the capability flags — the seven verbs the backend resolves.
const CAPABILITY_COLUMNS: Array<{ key: keyof EffectiveCapabilityFlags; label: string }> = [
	{ key: "view", label: "View" },
	{ key: "comment", label: "Comment" },
	{ key: "create", label: "Create" },
	{ key: "edit", label: "Edit" },
	{ key: "approve", label: "Approve" },
	{ key: "close", label: "Close" },
	{ key: "delete", label: "Delete" },
];

// Human labels for the non-global scope types shown in the "Project & RFx" tab.
const SCOPE_TYPE_LABEL: Record<Exclude<ScopeType, "global">, string> = {
	project: "Project",
	rfx: "RFx",
	project_type: "Project type",
};

const CapabilityDot = ({ granted }: { granted: boolean }) => {
	if (granted) {
		return <span className="text-emerald-600" aria-label="allowed">✓</span>;
	}

	return <span className="text-gray-300" aria-label="not allowed">—</span>;
};

/**
 * PRO-413 #UM1 — read-only view of a single user's access.
 *  - "Company-wide" tab (default): EFFECTIVE standing access as a module × capability
 *    matrix, straight from GET /rbac/users/{user}/permissions (H1 standing-only).
 *  - "Project & RFx" tab: the user's resource-scoped role assignments (project /
 *    RFx events), from the assignments record — the access that a company-wide view
 *    can't express. Shown only when the user has such grants.
 * Server-authoritative throughout; the FE never derives access itself.
 */
export const UserAccessMatrixModal = ({
	isOpen,
	userId,
	userName,
	onClose,
}: UserAccessMatrixModalProps) => {
	const modulesQuery = useModules();
	const permissionsQuery = useUserEffectivePermissions(isOpen ? userId : undefined);
	const { users: assignmentUsers } = useUserAssignments({ enabled: isOpen });

	const [tab, setTab] = useState<AccessTab>("company");

	const isLoading = modulesQuery.isLoading || permissionsQuery.isLoading;
	const isError = modulesQuery.isError || permissionsQuery.isError;

	const modules = modulesQuery.data ?? [];
	const permissions = permissionsQuery.data;
	const roles = permissions?.roles ?? [];

	// Resource-scoped (non-global) assignments for this user — the "Project & RFx"
	// tab. Company-wide (global) rows are represented by the standing matrix instead.
	const scopedAssignments = useMemo(() => {
		const record = assignmentUsers.find((entry) => entry.id === userId);
		return (record?.assignments ?? []).filter((assignment) => assignment.scope_type !== "global");
	}, [assignmentUsers, userId]);

	const title = userName ? `Access for ${userName}` : "User access";

	const tabButtonClass = (active: boolean): string =>
		`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
			active
				? "border-blue-600 text-blue-700"
				: "border-transparent text-gray-500 hover:text-gray-700"
		}`;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			description="This user's resolved access — company-wide, plus any project or RFx-specific grants."
			size="xxl"
			primaryButton={{ text: "Close", btntype: "secondary", onClick: onClose }}
		>
			<div data-testid="user-access-matrix-modal" className="space-y-4">
				{roles.length > 0 && (
					<div className="flex flex-wrap items-center gap-2" data-testid="user-access-roles">
						<span className="text-sm font-medium text-gray-600">Roles:</span>
						{roles.map((role) => (
							<span
								key={role}
								className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
							>
								{role}
							</span>
						))}
					</div>
				)}

				{/* Tabs: default to the company-wide matrix; the scoped tab appears only
				    when the user actually has project/RFx grants. */}
				<div className="flex items-center gap-1 border-b border-gray-200" role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={tab === "company"}
						className={tabButtonClass(tab === "company")}
						onClick={() => setTab("company")}
						data-testid="user-access-tab-company"
					>
						Company-wide
					</button>
					{scopedAssignments.length > 0 && (
						<button
							type="button"
							role="tab"
							aria-selected={tab === "scoped"}
							className={tabButtonClass(tab === "scoped")}
							onClick={() => setTab("scoped")}
							data-testid="user-access-tab-scoped"
						>
							Project &amp; RFx access
							<span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600">
								{scopedAssignments.length}
							</span>
						</button>
					)}
				</div>

				{isLoading && (
					<div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
						<Spinner size="sm" /> Loading access…
					</div>
				)}

				{!isLoading && isError && (
					<div className="py-10 text-center text-sm text-red-600" data-testid="user-access-error">
						Could not load this user&apos;s access. Please try again.
					</div>
				)}

				{!isLoading && !isError && tab === "company" && (
					<div className="max-h-[55vh] overflow-auto rounded-lg border border-gray-200">
						<table className="min-w-full border-collapse text-sm">
							<thead className="sticky top-0 z-10">
								<tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
									<th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 font-semibold">Module</th>
									<th className="bg-gray-50 px-3 py-2 font-semibold">Level</th>
									{CAPABILITY_COLUMNS.map((column) => (
										<th key={column.key} className="bg-gray-50 px-3 py-2 text-center font-semibold">
											{column.label}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{modules.map((module) => {
									const capability = permissions?.modules[module.key];
									const level: PermissionLevel = capability?.level ?? "none";
									const display = LEVEL_DISPLAY[level];

									return (
										<tr
											key={module.id}
											className="border-t border-gray-100"
											data-testid={`user-access-row-${module.key}`}
										>
											<th
												scope="row"
												className="sticky left-0 z-[1] bg-white px-3 py-2 text-left font-medium text-gray-700"
											>
												{module.name}
											</th>
											<td className="px-3 py-2">
												<span
													className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${display.fillClassName} ${display.textClassName}`}
												>
													{display.label}
												</span>
											</td>
											{CAPABILITY_COLUMNS.map((column) => (
												<td key={column.key} className="px-3 py-2 text-center">
													<CapabilityDot granted={Boolean(capability?.can[column.key])} />
												</td>
											))}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{!isLoading && !isError && tab === "scoped" && (
					<div className="max-h-[55vh] overflow-auto" data-testid="user-access-scoped-list">
						{scopedAssignments.length === 0 ? (
							<p className="py-10 text-center text-sm text-gray-500" data-testid="user-access-scoped-empty">
								No project or RFx-specific access.
							</p>
						) : (
							<ul className="flex flex-col gap-2">
								{scopedAssignments.map((assignment) => (
									<li
										key={assignment.assignment_id}
										className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5"
										data-testid={`user-access-scoped-row-${assignment.assignment_id}`}
									>
										<div className="flex flex-col">
											<span className="text-sm font-medium text-gray-800">{assignment.role_name}</span>
											<span className="text-xs text-gray-500">
												{assignment.scope_label ?? "—"}
											</span>
										</div>
										<span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
											{assignment.scope_type === "global"
												? "Company-wide"
												: SCOPE_TYPE_LABEL[assignment.scope_type]}
										</span>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>
		</Modal>
	);
};
